/* eslint-disable max-classes-per-file */

import { TextOptions } from '@storebrand-digital/fms-translations-react/dist/helpers/types';

/**
 * Unwrap<Promise<string>> = string
 * Unwrap<Array<string>> = string
 */
export type Unwrap<T> = T extends (infer U)[]
  ? U
  : T extends Promise<infer U>
  ? U
  : T;

export function delay(ms = 1000) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

export function pipe<T>(...functions: Array<(arg: T) => T>) {
  return (value: T) =>
    functions.reduce(
      (currentValue, currentFunc) => currentFunc(currentValue),
      value
    );
}

export class AbortError extends Error {
  constructor() {
    super('Aborted');
    this.name = this.constructor.name;
  }
}

export class TimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = this.constructor.name;
  }
}

export async function timeout(ms = 1000): Promise<never> {
  await delay(ms);
  throw new TimeoutError('Timeout');
}

/**
 * Format strings with basic placeholders
 *
 * ```
 * assert(format("Hello {name}", { name: "World" }) === "Hello World")
 * ```
 */
export function format(
  message: string | null | undefined,
  args: { [key: string]: any }
) {
  const placeholderPattern = Object.keys(args).join('|');
  return (message || '').replace(
    new RegExp(`{(${placeholderPattern})}`, 'g'),
    (placeholder, match) => args[match] || placeholder
  );
}

// https://stackoverflow.com/a/18650828
export function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) {
    return '0 B';
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Format strings with basic placeholders with fallback
 *
 * ```
 * assert(formatWithFallback("Hello {name}", { name: "World" }) === "Hello World")
 * assert(formatWithFallback("Hello {name}", {}, () => "Fallback") === "Hello Fallback")
 * ```
 */
export function getTextAndFormatWithFallback(
  message: string,
  args: { [key: string]: unknown },
  getText: (key: string) => string
) {
  const resolvedText = fallbackToRaw(message, getText);
  const formattedText = format(resolvedText, args);
  if (formattedText.match(/{.*}/)) {
    const fallback = fallbackToRaw(`${message}.fallback`, getText);
    return format(fallback, args);
  }
  return formattedText;
}

export function fallbackToRaw(
  key: string,
  getTextFunction: (fmsKey: string, options?: TextOptions) => string
) {
  const value = getTextFunction(key, { onMissingKey: () => '' });

  if (value == null) {
    return key;
  }
  return value;
}

export function stripWhitespace(input: string) {
  return input.replace(/[.\s]/g, '');
}
